import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Upload, CheckCircle2, ExternalLink } from 'lucide-react';
import SEO from '@/components/SEO';

/**
 * Reusable Content Page Template
 * Optimized for AI extraction and conversion
 */
export default function ContentPageTemplate({ 
  title,
  metaDescription,
  canonicalPath,
  directAnswer,
  sections = [],
  relatedLinks = [],
  ctaText = "Upload Photos & Get Quote",
  ctaSubtext = "Most quotes delivered within 24 hours",
  breadcrumbs = []
}) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO title={title} description={metaDescription} path={canonicalPath} />
      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <nav className="mb-6 text-sm text-[var(--brand-slate-600)] flex items-center gap-2">
            <button 
              onClick={() => navigate('/')}
              className="hover:text-[var(--brand-ocean-700)] transition-colors inline-flex items-center gap-1"
            >
              <ArrowLeft className="h-3 w-3" />
              <span>Back</span>
            </button>
            <span className="text-[var(--brand-slate-400)]">|</span>
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className="inline-flex items-center">
                {i > 0 && <span className="mx-2 text-[var(--brand-slate-400)]">/</span>}
                {crumb.link ? (
                  <a 
                    href={crumb.link} 
                    onClick={(e) => { e.preventDefault(); navigate(crumb.link); }}
                    className="hover:text-[var(--brand-ocean-700)] transition-colors"
                  >
                    {crumb.label}
                  </a>
                ) : (
                  <span className="text-[var(--brand-slate-900)] font-medium">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}

        {/* H1 Title (AI-optimized, question format) */}
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[var(--brand-slate-900)] leading-[1.15] mb-6">
          {title}
        </h1>

        {/* Direct Answer Summary (AI extractable) */}
        {directAnswer && (
          <Card className="rounded-2xl shadow-sm border-2 border-[var(--brand-ocean-200)] bg-[var(--brand-ocean-50)] mb-8">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center shrink-0 mt-1">
                  <CheckCircle2 className="h-5 w-5 text-[var(--brand-ocean-700)]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[var(--brand-ocean-900)] mb-2">Quick Answer</p>
                  <div className="text-sm text-[var(--brand-slate-700)] leading-relaxed">
                    {directAnswer}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content Sections */}
        {sections.map((section, i) => (
          <section key={i} className="mb-10" data-testid={section.testId}>
            <h2 className="font-heading text-2xl font-bold text-[var(--brand-slate-900)] mb-4">
              {section.title}
            </h2>
            
            {section.content && (
              <div className="prose prose-sm max-w-none text-[var(--brand-slate-600)] leading-relaxed mb-4">
                {section.content}
              </div>
            )}

            {section.bullets && (
              <ul className="space-y-2 mb-4">
                {section.bullets.map((bullet, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span className="text-sm text-[var(--brand-slate-600)] leading-relaxed">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.table && (
              <div className="overflow-x-auto mb-4">
                <table className="w-full border border-[var(--brand-slate-200)] rounded-lg overflow-hidden">
                  <thead className="bg-[var(--brand-ocean-50)]">
                    <tr>
                      {section.table.headers.map((header, j) => (
                        <th key={j} className="px-4 py-3 text-left text-sm font-semibold text-[var(--brand-slate-900)]">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.table.rows.map((row, j) => (
                      <tr key={j} className="border-t border-[var(--brand-slate-200)]">
                        {row.map((cell, k) => (
                          <td key={k} className="px-4 py-3 text-sm text-[var(--brand-slate-600)]">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {section.highlight && (
              <Card className="rounded-xl bg-[var(--brand-sand-50)] border-2 border-[var(--brand-sand-200)] mb-4">
                <CardContent className="p-5">
                  <p className="text-sm text-[var(--brand-slate-700)] leading-relaxed">
                    <strong className="text-[var(--brand-slate-900)]">Note:</strong> {section.highlight}
                  </p>
                </CardContent>
              </Card>
            )}
          </section>
        ))}

        {/* Related Links */}
        {relatedLinks.length > 0 && (
          <section className="mb-10">
            <h2 className="font-heading text-xl font-bold text-[var(--brand-slate-900)] mb-4">
              Related Information
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {relatedLinks.map((link, i) => (
                <Button
                  key={i}
                  variant="outline"
                  onClick={() => navigate(link.url)}
                  className="justify-start h-auto py-3 px-4 text-left rounded-xl border-2 border-[var(--brand-ocean-200)] hover:bg-[var(--brand-ocean-50)] hover:border-[var(--brand-ocean-300)]"
                >
                  <div className="flex items-center gap-2 w-full">
                    <ExternalLink className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[var(--brand-slate-900)]">{link.title}</p>
                      {link.description && (
                        <p className="text-xs text-[var(--brand-slate-600)] mt-0.5">{link.description}</p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-[var(--brand-slate-400)]" />
                  </div>
                </Button>
              ))}
            </div>
          </section>
        )}

        {/* CTA Block */}
        <Card className="rounded-2xl shadow-lg bg-gradient-to-br from-[var(--brand-ocean-700)] to-[var(--brand-ocean-900)] border-0 overflow-hidden">
          <CardContent className="p-8 text-center">
            <h3 className="font-heading text-2xl font-bold text-white mb-2">
              {ctaText}
            </h3>
            <p className="text-white/80 text-sm mb-6">
              {ctaSubtext}
            </p>
            <Button 
              onClick={() => navigate('/get-quote')}
              size="lg"
              data-testid="content-page-cta"
              className="bg-white text-[var(--brand-ocean-900)] rounded-xl h-14 px-8 text-base font-semibold shadow-xl hover:bg-white/95"
            >
              <Upload className="mr-2 h-5 w-5" />
              Upload Photos Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-xs text-white/60 mt-3">
              No appointment • No pressure • Free quote
            </p>
          </CardContent>
        </Card>
      </article>
    </div>
  );
}
